import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link } from 'react-router-dom';
import type { FootprintItem } from '@/config/footprints';

interface FootprintCarouselProps {
  items: FootprintItem[];
}

const CARD_WIDTH = 280;
const CARD_GAP = 20;

export default function FootprintCarousel({ items }: FootprintCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, items.length]);

  const scrollByCards = useCallback((direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    const visibleCards = Math.max(
      1,
      Math.floor(el.clientWidth / (CARD_WIDTH + CARD_GAP)),
    );
    el.scrollBy({
      left: direction * visibleCards * (CARD_WIDTH + CARD_GAP),
      behavior: 'smooth',
    });
  }, []);

  if (items.length === 0) {
    return null;
  }

  const arrowSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 3,
    border: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
    color: '#1f1f1f',
    boxShadow: '0 2px 10px rgba(16, 24, 40, 0.12)',
    '&:hover': { backgroundColor: '#f3f4f6' },
  } as const;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 8,
          width: { xs: 32, md: 72 },
          zIndex: 2,
          pointerEvents: 'none',
          background:
            'linear-gradient(to right, #ffffff 0%, rgba(255,255,255,0) 100%)',
          opacity: canScrollLeft ? 1 : 0,
          transition: 'opacity 160ms ease',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 8,
          width: { xs: 32, md: 72 },
          zIndex: 2,
          pointerEvents: 'none',
          background:
            'linear-gradient(to left, #ffffff 0%, rgba(255,255,255,0) 100%)',
          opacity: canScrollRight ? 1 : 0,
          transition: 'opacity 160ms ease',
        }}
      />

      {canScrollLeft && (
        <IconButton
          aria-label="Scroll to previous footprints"
          onClick={() => scrollByCards(-1)}
          sx={{ ...arrowSx, left: { xs: 4, md: 8 } }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}
      {canScrollRight && (
        <IconButton
          aria-label="Scroll to next footprints"
          onClick={() => scrollByCards(1)}
          sx={{ ...arrowSx, right: { xs: 4, md: 8 } }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: `${CARD_GAP}px`,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          pb: 1,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {items.map((item) => (
          <Box
            key={item.title}
            component={Link}
            to={item.link}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              flex: `0 0 ${CARD_WIDTH}px`,
              width: `${CARD_WIDTH}px`,
              scrollSnapAlign: 'start',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              borderRadius: 3,
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
              backgroundColor: '#ffffff',
              transition:
                'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                borderColor: '#c6dafc',
                boxShadow: '0 6px 20px rgba(26, 115, 232, 0.15)',
              },
              '&:hover .card-banner': {
                transform: 'scale(1.04)',
              },
              '&:hover .card-explore': {
                backgroundColor: '#e69200',
                boxShadow: '0 4px 10px rgba(255, 162, 0, 0.45)',
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 140,
                overflow: 'hidden',
                backgroundColor: '#e5e7eb',
              }}
            >
              <Box
                className="card-banner"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url(${item.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  transition: 'transform 240ms ease',
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                p: 2.25,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontWeight: 800,
                  fontSize: '1rem',
                  mb: 0.75,
                  display: 'inline-block',
                  color: '#202124',
                  lineHeight: 1.3,
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#3c4043',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  mb: 2,
                }}
              >
                {item.description}
              </Typography>

              <Box
                component="span"
                className="card-explore"
                sx={{
                  mt: 'auto',
                  alignSelf: 'flex-start',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 1.75,
                  py: 0.65,
                  borderRadius: 1.25,
                  backgroundColor: '#ffa200',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.825rem',
                  letterSpacing: 0.2,
                  boxShadow: '0 2px 6px rgba(255, 162, 0, 0.35)',
                  transition:
                    'background-color 140ms ease, box-shadow 140ms ease',
                }}
              >
                Explore
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
